<template>
	<div id="options-modal-wrapper" class="wrapper" ref="options-modal">
		<!-- header:start -->
		<div class="wrapper-header">
			<div class="button-wrapper">
				<div class="left-side">
					<md-button class="md-icon-button" @click="handleBack" data-test="close-option-card">
						<base-icon class="back-icon" icon-name="arrow-back" />
					</md-button>
					<p class="H5-5 mobile-hide">Choose your {{ getStepName }} product</p>
				</div>

				<div class="right-side">
					<md-button
						:disabled="!getSelectedProduct || getSelectedProduct.includes('custom-product')"
						class="md-primary md-button-large md-raised apply-btn"
						:class="{
							'product-tour-step-2': isMobileView && hasProductGroups && step === 0,
							'product-tour-step-4':
								!isMobileView || (hasProductGroups && step === 1) || !hasProductGroups,
						}"
						v-on:click="handleNextStep"
					>
						<base-icon class="back-icon" icon-name="check" />

						{{ modalButtonText }}
					</md-button>
				</div>
			</div>
		</div>
		<!-- header:end -->

		<div class="options-modal">
			<OptionsTags :options="options" />

			<template v-if="!chipsView">
				<filters-list
					v-if="doWeShowFilters"
					:filter-options="getFilterCategories"
					v-model="selectedFilters"
					@input="onSelectedFilters"
				/>

				<!-- options:start -->
				<div class="options-modal-items">
					<OptionsCard
						class="options-card options"
						:class="index == 0 ? 'product-tour-step-1' : ''"
						v-for="(card, index) in productsToShow"
						:key="card.pimId"
						:cardId="card.key || card.map((el) => el.key)"
						:stepName="options.name"
						:selectedProduct="getSelectedProduct"
						:cardDetails="{card, index}"
						:country="getCountry"
						:addCustomProduct="Boolean(card.customContent)"
						:optionName="options.name"
						:productIndex="getProductIndex"
						:productGroupsState="productGroupsState"
						@requestDemo="$emit('requestDemo')"
						@sendToInquireLink="$emit('sendToInquireLink', $event)"
						@sendToGeneGlobeLink="$emit('sendToGeneGlobeLink', $event)"
						@logInRequested="$emit('logInRequested', $event)"
						@showCustomProductModal="$emit('showCustomProductModal', $event)"
						@selectOption="setSelectedProduct"
						@extendProductGroups="toggelProductGroupsState"
						@onCardSelected="onCardSelected"
						:showProductGroups="!isMobileView"
						:data-test="`option-product-for-selection-${card.catalogNumber}`"
					/>
					<OptionsCard
						v-if="customProductLength < 20 && !chipsView"
						class="options-card custom-product"
						:stepName="options.name"
						:selectedProduct="getSelectedProduct"
						cardId="custom-product"
						:country="getCountry"
						:productIndex="getProductIndex"
						@showCustomProductModal="$emit('showCustomProductModal', $event)"
						:addCustomProduct="true"
					/>
				</div>
				<!-- options:end -->

				<div class="products-with-rules" v-if="getProductsWithRules.length">
					<h5 class="mobile-headline-6">Not available with selected products:</h5>
					<div class="options-modal-items">
						<OptionsCard
							class="options-card options"
							v-for="(card, index) in getProductsWithRules"
							isDisabled
							:key="card.pimId"
							:cardId="card.key"
							:stepName="options.name"
							:selectedProduct="getSelectedProduct"
							:cardDetails="{card, index}"
							:country="getCountry"
							:addCustomProduct="Boolean(card.customContent)"
							:optionName="options.name"
							:productIndex="getProductIndex"
						/>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="options-modal-items">
					<OptionsCard
						class="options-card options options-card-mobile"
						:key="'option-card-mobile'"
						:cardId="getSelectedProduct"
						:stepName="options.name"
						:selectedProduct="getSelectedProduct"
						:cardDetails="selectedCardDetails"
						:country="getCountry"
						:addCustomProduct="false"
						:optionName="options.name"
						:productIndex="getProductIndex"
						:productGroupsState="productGroupsState"
						@requestDemo="$emit('requestDemo')"
						@sendToInquireLink="$emit('sendToInquireLink', $event)"
						@sendToGeneGlobeLink="$emit('sendToGeneGlobeLink', $event)"
						@logInRequested="$emit('logInRequested', $event)"
						@showCustomProductModal="$emit('showCustomProductModal', $event)"
						@extendProductGroups="toggelProductGroupsState"
						@selectOption="setSelectedProduct"
						:showProductGroups="true"
					/>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import {trackEvent} from '@/services/tracking.js';
import clonedeep from 'lodash.clonedeep';
import groupProducts from '@/mixins/groupProducts.js';
import FiltersList from './FiltersList.vue';
import OptionsCard from './OptionCard.vue';
import mobileView from '@/mixins/mobileView';
import intersection from '@/components/modals/mixins/intersection';
import tour from '@/mixins/tour';
import OptionsTags from './OptionsTags.vue';

export default {
	name: 'OptionsModal',
	components: {FiltersList, OptionsCard, OptionsTags},
	mixins: [groupProducts, mobileView, intersection, tour],
	props: {
		options: {
			type: Object,
		},
		lang: {
			type: String,
		},
		elementInView: {
			type: Function,
		},
	},
	created() {
		this.$root.$emit('closeGlobalBanner');
		this.setModalType();
	},
	mounted() {
		this.setInitalSelectedProduct();
		this.addObserverHeader();
		window.addEventListener('resize', this.handleResize);
		this.getWindowSize();
		setTimeout(() => {
			this.startTour('product');
		}, 500);
		this.prepareFiltersList();
		this.$store.dispatch('tags/setActiveStep', {step: this.options.name, products: this.options.products});
	},
	beforeDestroy() {
		this.removeObserverHeader();
		window.removeEventListener('resize', this.handleResize);
	},
	data: () => ({
		selectorClasses: ['options-modal-item', 'options-modal-item-content-top', 'options-modal-item-content-bottom'],
		showDropdown: false,
		isElementInView: null,
		selectedFilters: {},
		selectedProduct: null,
		productGroupsState: false,
		chipsView: false,
		selectedCardDetails: null,
		filterCategories: null,
		seeAllModal: null,
		step: 0,
	}),
	computed: {
		doesProductHaveChips() {
			return !!(this.selectedCardDetails?.card?.length > 1);
		},
		doWeShowFilters() {
			if (!this.$store.state.showProductsFilter || this.options?.products?.length <= 1) return false;
			// if there are filters with more than one filter, show them
			return Object.values(this.filterCategories || {}).some((cat) => cat.filterGroup.filters.length > 1);
		},
		hasProductGroups() {
			return this.selectedCardDetails?.card?.length > 0;
		},
		customProductLength() {
			return this.options.selected.filter((selected) => selected.initial.includes('custom-product')).length;
		},
		filteredProductList() {
			const fullList = this.getFilterableProducts;

			const selectObj = this.getSelectedFilters;
			this.buildFilterOptions(fullList);
			if (!selectObj || !Object.values(selectObj).flat().length) return fullList;
			return this.filterList(selectObj, fullList);
		},

		getCountry() {
			return this.$store.state.jwt ? this.$store.state.jwt.country : this.$store.state.COUNTRY;
		},
		getFilterCategories() {
			return this.filterCategories;
		},
		getFilterableProducts() {
			return this.options.products.filter(
				(prod) =>
					this.matchesTagsSelection(prod) &&
					!prod?.customContent &&
					prod.rules.excludeBy.size === 0 &&
					this.doWeShowExclusiveByProduct(prod) &&
					this.doWeShowSelectedProduct(prod),
			);
		},
		getProductIndex() {
			return this.options.productIndex;
		},
		getProductsWithRules() {
			return this.options.products.filter((prod) => {
				// remove custom products and products without rules
				if (prod?.customContent || !prod?.rules) return false;

				// keep excluded products && exclusively removed products that are currently selected
				return (
					prod.rules.excludeBy.size > 0 ||
					(prod.rules.exclusiveBy.size > 0 && !this.options?.selected.some((sel) => sel.initial === prod.key))
				);
			});
		},
		getSelectedFilters() {
			return this.selectedFilters;
		},
		getSelectedProduct() {
			return this.selectedProduct;
		},
		getStepName() {
			if (this.$store.state.cvlWebSteps) {
				let name;
				this.$store.state.cvlWebSteps.values.forEach((step) => {
					if (step.key === this.options.name) {
						name = step.value[this.lang] || step.value.enUS;
					}
				});
				return name;
			}
		},
		modalButtonText() {
			return this.isMobileView && this.doesProductHaveChips && !this.chipsView ? 'NEXT' : 'ADD TO EXPERIMENT';
		},
		productsToShow() {
			return this.groupProducts(this.filteredProductList);
		},
		isSeeAllModal() {
			return this.seeAllModal;
		},
	},
	methods: {
		productTags(product) {
			return this.$store.getters['tags/productTagAssignments'](product);
		},
		productAlternativeProductsTags(product) {
			let activeTags = this.$store.getters['tags/activeAlternativeProductsTags'];
			let activeIds = Object.keys(activeTags).map((id) => Number.parseInt(id));
			let retval = this.productTags(product)
				.filter((tag) => activeIds.includes(tag['group-id']))
				.reduce((acc, tag) => {
					if (!acc[tag['group-id']]) acc[tag['group-id']] = [];
					acc[tag['group-id']].push(tag.id);
					return acc;
				}, {});
			return retval;
		},
		matchesTagsSelection(product) {
			let activeAlternativeProductsTags = this.$store.getters['tags/activeAlternativeProductsTags'];
			let productHasNoTags = Object.keys(this.productAlternativeProductsTags(product)).length === 0;
			if (productHasNoTags) {
				return true;
			}
			return !Object.keys(this.productAlternativeProductsTags(product))
				.map((groupId) => {
					let noSelectedTagsInGroup = activeAlternativeProductsTags[groupId].size === 0;
					let productHasSelectedTagInGroup = this.productAlternativeProductsTags(product)[groupId].some(
						(tag) =>
							activeAlternativeProductsTags[groupId]
								? activeAlternativeProductsTags[groupId].has(tag)
								: false,
					);
					return noSelectedTagsInGroup || productHasSelectedTagInGroup;
				})
				.includes(false);
		},
		setModalType() {
			const {productIndex, selected} = this.options;
			this.seeAllModal = productIndex !== selected.length;
		},
		buildFilterOptions(products) {
			this.filterCategories = this.buildFilterCategories(products);
		},
		buildFilterCategories(products) {
			/*
        Build Sets() for each product category
        ie: Application: Set(3){"cloning", "automateddnafragmentandrnaanalysis", "fusiongene"}
      */

			const filterSets = this.getFilterSets(products);
			/**
			 *  Builds filterCategories for each workflowstep
			 */
			const filterCategories = {};
			for (const filterKey in filterSets) {
				if (filterKey) {
					// name of Set()
					// find where the value.key === name of Set() ie: Application === Application
					const filterGroup = this.$store.state.cvlFilter.values.find((value) => value.key === filterKey);

					// update order of filterSets
					const filterSetSorted = [...filterSets[filterKey]].sort();

					// add children that are also in the filterSet
					const filterGroupChildArry = filterGroup.children.filter((child) =>
						filterSetSorted.includes(child.key),
					);
					filterCategories[filterGroup.key] = {
						key: filterGroup.key,
						name: filterGroup.value,
						filterGroup: {
							filters: filterGroupChildArry,
							selectedFilters: {
								temp: [],
								updated: [],
							},
						},
					};
				}
			}
			return filterCategories;
		},
		getFilterSets(products) {
			const filterSets = {};
			// for each product, build filter sets by category
			products.forEach((product) => {
				// each product in a workstep
				product.filterCategories.forEach((category) => {
					// Create a Set for each filterCategories.catergory.key; ie: Application, Instrument, Detction
					if (!Object.prototype.hasOwnProperty.call(filterSets, category.key)) {
						filterSets[category.key] = new Set();
					}
					if (category.children) {
						category.children.forEach((child) => {
							// Add category to set ie:
							// Application: Set(3){"cloning", "automateddnafragmentandrnaanalysis", "fusiongene"}
							filterSets[category.key].add(child.key);
						});
					}
				});
			});
			return filterSets;
		},
		closeModal() {
			// user clicked close before applying, reset selected.update to selected.initial
			this.options.selected.updated = this.options.selected.initial;
			this.$emit('closeOptionsModal');
		},
		doWeShowExclusiveByProduct(prod) {
			// true if product is not exclusively removed or if it is by exclusively required by another product
			return (
				prod.rules.exclusiveBy.size === 0 ||
				this.options.selected[this.options.productIndex]?.initial === prod.key
			);
		},
		doWeShowSelectedProduct(prod) {
			return !this.isSeeAllModal
				? // don't show selected if user is Adding Product
				  !this.options.selected.some((pr) => pr.initial === prod.key)
				: // show product the user is changing but not any other selected product
				  this.options.selected[this.options.productIndex].initial === prod.key ||
						!this.options.selected.some((pr) => pr.initial === prod.key);
		},
		filterByCategory(listProducts, filterCategory, filterItem) {
			if (!filterItem.length) return listProducts;

			return listProducts.filter((product) => {
				const productFilterCategory = product.filterCategories.find((filter) => filter.key === filterCategory);
				if (!productFilterCategory) return false;
				const productFilterSubCategory = productFilterCategory.children.map((subCat) => subCat.key);
				return productFilterSubCategory.some((productFilter) => filterItem.includes(productFilter));
			});
		},
		filterList(selectObj, fullList) {
			let tempFilterList = fullList;
			for (let [filterCategory, filterItem] of Object.entries(selectObj))
				tempFilterList = this.filterByCategory(tempFilterList, filterCategory, filterItem);
			return tempFilterList;
		},

		handleBack() {
			!this.chipsView ? this.closeModal() : (this.chipsView = false);
			this.step = 0;
		},
		handleNextStep() {
			this.isMobileView && this.doesProductHaveChips && !this.chipsView
				? (this.chipsView = true)
				: this.updateWorkflowContainer();
			this.step = 1;
		},
		onCardSelected(card) {
			this.selectedCardDetails = card;
		},
		async onSelectedFilters(filters) {
			this.$store.commit('setStepProductFilters', {step: this.options.name, filters});
			await this.$nextTick();
			this.$forceUpdate();
		},
		prepareFiltersList() {
			// get selected filters from store
			const selectedFiltersFromStore = this.$store.state.stepProductFilters[this.options.name] || {};

			// prep selected filters object for available products passed to modal
			for (const prop in this.filterCategories) this.$set(this.selectedFilters, prop, []);

			// updated selected filters if filters are available and saved in store
			for (const [key, value] of Object.entries(selectedFiltersFromStore))
				if (key in this.selectedFilters) this.selectedFilters[key] = value;
		},
		setInitalSelectedProduct() {
			const {productIndex, selected} = this.options;
			// if 'See All Products' btn triggered the modal and the selected product is not a custom product
			if (this.isSeeAllModal && !selected[productIndex].initial.includes('custom-product')) {
				// need to tigger a click event so the child can emit the card after it is built

				if (this.$el.querySelector)
					this.$el.querySelector(`[cardid*="${selected[productIndex].initial}"]`)?.click();
				this.setSelectedProduct(selected[productIndex].initial);
			}
		},
		setSelectedProduct(key) {
			this.selectedProduct = key;
		},
		toggelProductGroupsState() {
			this.productGroupsState = !this.productGroupsState;
		},
		async updateWorkflowContainer() {
			trackEvent('ctaClick', {eVar58: 'Add to Workflow | Workflow Configurator'});
			const selectedIndex = +this.getProductIndex;
			const selectedProduct = this.getSelectedProduct;

			if (this.options.selected.some((prod) => prod.initial === selectedProduct))
				return this.$emit('closeOptionsModal');

			const options = clonedeep(this.options);
			const addProduct = selectedProduct;
			const removeProduct = options.selected[selectedIndex]?.initial || null;

			if (!options.selected[selectedIndex]) {
				options.selected.push({initial: '', updated: ''});
			}
			if (options.selected[selectedIndex].updated !== selectedProduct) {
				options.selected[selectedIndex].initial = this.getSelectedProduct;
				options.selected[selectedIndex].updated = this.getSelectedProduct;
				this.$emit('doWeShowRuleModal', {wfs: options, addProduct, removeProduct});
			}
		},
	},
};
</script>
<style lang="scss">
#options-modal-wrapper {
	&.wrapper {
		@include box(100%, auto);
		position: fixed;
		top: 0;
		left: 0;
		height: 100vh;
		background: $color-extra-light-grey;
		z-index: 10000;
		overflow: auto;
		padding-top: 65px;
		@media (max-width: $tablet) {
			padding-top: 90px;
		}
		@media (max-width: $mobile) {
			background: $white;
			padding-top: 65px;
		}

		.wrapper-header {
			position: fixed;
			top: 0;
			width: 100%;
			background: $white;
			z-index: 10000;
			box-shadow: 0px 2px 4px rgba(8, 35, 48, 0.24);

			.button-wrapper {
				padding: 12px 32px 12px 32px;

				@media (max-width: $desktop) {
					padding: 12px 26px 12px 10px;
				}

				margin: 0 auto;
				display: flex;
				flex-direction: row;
				justify-content: space-between;

				.md-button {
					margin: 0;
				}
			}
			.left-side {
				display: flex;
				align-items: center;
				padding-right: 10px;
				p {
					margin: 0;
					padding-left: 10px;
				}
			}
			.right-side {
				display: flex;
				justify-content: center;
				justify-items: center;
				flex-direction: column;
				button {
					max-height: 40px;
					@media (min-width: $mobile + 1) {
						.md-ripple {
							padding: 8px 16.5px;
						}
						.icons {
							margin-right: 1px;
						}
					}
				}
			}
		}

		.options-modal {
			max-width: 1236px;
			margin: 0 auto;
			padding: 0 10px;
			@media (max-width: 992px) and (min-width: 701px) {
				padding-bottom: 1.4rem;
			}
			&-items {
				position: relative;
				z-index: 1;
				display: grid;
				gap: 24px;
				grid-template-columns: repeat(4, 1fr);
				padding: 20px 0;
				justify-content: space-evenly;

				@media (max-width: $tablet) {
					grid-template-columns: repeat(3, 1fr);
				}

				@media (max-width: $mobile) {
					grid-template-columns: repeat(1, 1fr);

					.options-card {
						min-width: 100%;
					}

					&.custom-product:hover {
						cursor: pointer;
					}
				}
			}
		}
		.products-with-rules {
			padding-top: 28px;
			h5 {
				margin-top: 0;
			}
		}
	}
}
</style>
